import AppLogo from "@/components/AppLogo";
import { Button } from "@/components/ui/button/button";
import { Input } from "@/components/ui/input/input";
import { type FormEvent, type ReactNode, useCallback, useState } from "react";

const BYPASS_STORAGE_KEY = "spc_kashu_maintenance_session_bypass";

function readBypassFromStorage(): boolean {
	try {
		return sessionStorage.getItem(BYPASS_STORAGE_KEY) === "1";
	} catch {
		return false;
	}
}

type MaintenanceGateProps = {
	enabled: boolean;
	bypassPassword: string;
	children: ReactNode;
};

export function MaintenanceGate({
	enabled,
	bypassPassword,
	children,
}: MaintenanceGateProps) {
	const [bypassed, setBypassed] = useState(readBypassFromStorage);
	const [password, setPassword] = useState("");
	const [error, setError] = useState<string | null>(null);

	const onSubmit = useCallback(
		(e: FormEvent<HTMLFormElement>) => {
			e.preventDefault();
			if (password === bypassPassword) {
				try {
					sessionStorage.setItem(BYPASS_STORAGE_KEY, "1");
				} catch {
					// If storage is unavailable, still allow this session.
				}
				setError(null);
				setBypassed(true);
				setPassword("");
				return;
			}
			setError("Incorrect password.");
		},
		[bypassPassword, password],
	);

	if (!enabled || bypassed) {
		return <>{children}</>;
	}

	return (
		<div className="flex min-h-[100dvh] flex-col items-center justify-center gap-8 bg-primary-600 p-6 text-white">
			<AppLogo theme="light" />
			<div className="max-w-md space-y-2 text-center">
				<h1 className="text-2xl font-semibold">We&apos;ll be right back</h1>
				<p className="text-sm text-white/90">
					The app is temporarily unavailable while we perform maintenance.
				</p>
			</div>
			<form
				onSubmit={onSubmit}
				className="flex w-full max-w-sm flex-col gap-3 rounded-lg bg-white/10 p-4 backdrop-blur-sm"
			>
				<label className="text-left text-sm font-medium text-white">
					<Input
						type="password"
						name="password"
						autoComplete="off"
						value={password}
						onChange={(e) => {
							setPassword(e.target.value);
							if (error) setError(null);
						}}
						className="mt-1 border-white/30 bg-white/10 text-white placeholder:text-white/50"
					/>
				</label>
				{error ? (
					<p className="text-left text-sm text-red-200" role="alert">
						{error}
					</p>
				) : null}
				<Button type="submit" variant="secondary" className="w-full">
					Continue to app
				</Button>
			</form>
		</div>
	);
}
